import type React from "react";
import "./FloatingLayer.css";

/**
 * The layer every floating popup shares: popovers, menus, tooltips and select lists. Each one keeps
 * the matching Base UI parts for its job (a menu is a `Menu`, a tooltip a `Tooltip`), and takes its
 * placement, portal container, focus return, frame and open/close animation from here.
 */

export type FloatingPlacement =
  | "top"
  | "right"
  | "bottom"
  | "left"
  | "top-start"
  | "top-end"
  | "right-start"
  | "right-end"
  | "bottom-start"
  | "bottom-end"
  | "left-start"
  | "left-end";

export interface FloatingPositioningProps {
  placement?: FloatingPlacement;
  offset?: number;
  shift?: number;
  overlapReference?: boolean;
  avoidReferenceOverlap?: boolean;
  fillWidth?: boolean;
  clippingRef?: React.RefObject<HTMLElement | null>;
}

type Side = "top" | "right" | "bottom" | "left";
type Align = "start" | "center" | "end";
type OffsetData = { side: string; anchor: { width: number; height: number } };

/** Class for a popup element: gives it the shared open and close animation. */
export const FLOATING_POPUP_CLASS = "calmlens-floating-popup";
/** Class for a popup's surface: border, background, radius and shadow. */
export const FLOATING_PANEL_CLASS = "calmlens-floating-panel";

/** Props for any Base UI positioner (popover, menu, tooltip, select, combobox). */
export const getFloatingPositionerProps = (
  props: FloatingPositioningProps,
  overrides?: { positionMethod?: "absolute" | "fixed" },
) => {
  const [side, align] = (props.placement ?? "bottom").split("-") as [Side, Align | undefined];
  return {
    "data-popover": true,
    className: "calmlens-floating-positioner",
    side,
    align: props.overlapReference ? "start" : (align ?? "center"),
    sideOffset: props.overlapReference
      ? ({ side, anchor }: OffsetData) =>
          -(side === "top" || side === "bottom" ? anchor.height : anchor.width)
      : (props.offset ?? 10),
    collisionPadding: props.shift ?? 10,
    collisionBoundary: props.clippingRef?.current ?? "clipping-ancestors",
    collisionAvoidance: props.overlapReference
      ? ({ side: "none", align: "shift" } as const)
      : ({
          side: "flip",
          align: "shift",
          fallbackAxisSide: props.avoidReferenceOverlap ? "end" : "none",
        } as const),
    positionMethod: overrides?.positionMethod ?? "fixed",
  } as const;
};

/** Size variables a popup reads from its positioner. */
export const getFloatingPopupStyle = (fillWidth?: boolean): React.CSSProperties =>
  ({
    "--popover-available-height": "var(--available-height)",
    ...(fillWidth ? { width: "var(--anchor-width)" } : {}),
  }) as React.CSSProperties;

const DIALOG_SELECTOR = "[role='dialog'], [role='alertdialog']";

/**
 * Where a popup renders: the page's popover layer, except inside dialogs, whose focus trap and
 * backdrop block that layer, so there it renders inside the dialog.
 */
export const getFloatingContainer = (reference: Element | null): HTMLElement | null => {
  if (typeof document === "undefined") return null;
  const dialog = reference?.closest<HTMLElement>(DIALOG_SELECTOR);
  return dialog ?? document.querySelector<HTMLElement>("[data-popover-container]") ?? document.body;
};

/** Props for any Base UI portal, rendering into `container` or the shared container. */
export const getFloatingPortalProps = (
  reference: Element | null,
  container?: HTMLElement | null,
) => ({
  container: container ?? getFloatingContainer(reference),
  // The portal's own wrapper must not take up space in the layout it renders into.
  style: { position: "absolute" } as React.CSSProperties,
});

/**
 * A popup's `finalFocus`: closing returns focus to the trigger, unless the close opened a dialog
 * (such as a menu item that opens one). Returning focus then would pull it out of the dialog and
 * past its focus trap.
 */
export const restoreFocusUnlessDialogOpened =
  (getReference: () => Element | null, getFloating: () => Element | null) => (): boolean => {
    if (typeof document === "undefined") return true;
    const reference = getReference();
    const floating = getFloating();
    const dialogs = document.querySelectorAll(
      "[role='dialog'][data-open], [role='alertdialog'][data-open]",
    );
    // Dialogs that hold this trigger or this popup are its own surroundings, not a new dialog.
    return ![...dialogs].some(
      (dialog) =>
        dialog !== floating &&
        !(floating && dialog.contains(floating)) &&
        !(reference && dialog.contains(reference)),
    );
  };
